import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Modal, Select, Switch } from "antd";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import { FIRESTORE_DEALERS_TABLE } from "../../../constants/FirestoreConstant";
import { DEALER_COUNTRIES } from "../../../constants/DealerConstants";

const EditDealerModal = ({ visible, onCancel, dealer }) => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Pre-populate form when dealer data changes
  useEffect(() => {
    if (dealer && visible) {
      form.setFieldsValue({
        storeName: dealer.storeName,
        street: dealer.street,
        zipCode: dealer.zipCode,
        city: dealer.city,
        country: dealer.country,
        lat: dealer.lat.toString(),
        lon: dealer.lon.toString(),
        isPremium: dealer.tag === "Premium",
      });
    }
  }, [dealer, visible, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // Format the data for Firestore
      const dealerData = {
        ...values,
        lat: parseFloat(values.lat),
        lon: parseFloat(values.lon),
        tag: values.isPremium ? "Premium" : null,
        updatedAt: firestore.Timestamp.now(),
      };

      // Remove the isPremium field as it's just for the UI
      delete dealerData.isPremium;

      // Update Firestore document
      await firestore.collection(FIRESTORE_DEALERS_TABLE).doc(dealer.id).update(dealerData);

      message.success(t("notifications.entry_updated"));
      onCancel();
    } catch (error) {
      console.error("Error updating dealer:", error);
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("edit_dealer") || t("edit")}
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t("cancel")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
          {t("save")}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t("dealer_name")}
          name="storeName"
          rules={[{ required: true, message: t("form.enter_value") }]}
        >
          <Input placeholder={t("dealer_name")} />
        </Form.Item>

        <Form.Item label={t("street")} name="street" rules={[{ required: true, message: t("form.enter_value") }]}>
          <Input placeholder={t("street")} />
        </Form.Item>

        <Form.Item label={t("zip_code")} name="zipCode" rules={[{ required: true, message: t("form.enter_value") }]}>
          <Input placeholder={t("zip_code")} />
        </Form.Item>

        <Form.Item label={t("city")} name="city" rules={[{ required: true, message: t("form.enter_value") }]}>
          <Input placeholder={t("city")} />
        </Form.Item>

        <Form.Item label={t("country")} name="country" rules={[{ required: true, message: t("form.enter_value") }]}>
          <Select placeholder={t("form.choose_country")}>
            {DEALER_COUNTRIES.map((country) => (
              <Select.Option key={country.code} value={country.code}>
                {country.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={t("latitude")}
          name="lat"
          rules={[
            { required: true, message: t("form.enter_value") },
            {
              pattern: /^-?([1-8]?[1-9]|[1-9]0)\.?\d{0,15}$/,
              message: t("form.enter_value"),
            },
          ]}
        >
          <Input placeholder="51.5877" />
        </Form.Item>

        <Form.Item
          label={t("longitude")}
          name="lon"
          rules={[
            { required: true, message: t("form.enter_value") },
            {
              pattern: /^-?((1[0-7][0-9])|([1-9]?[0-9]))\.?\d{0,15}$/,
              message: t("form.enter_value"),
            },
          ]}
        >
          <Input placeholder="5.1971" />
        </Form.Item>

        <Form.Item label={t("premium")} name="isPremium" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditDealerModal;
